import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    XIcon,
    EnvelopeSimpleIcon,
    WhatsappLogoIcon,
    BellSimpleIcon,
    ClockCounterClockwiseIcon,
    WebhooksLogoIcon,
} from '@phosphor-icons/react';
import { AlertRuleActionType, FullAlertRule } from '@/types';
import { Button } from '@/components/ui/Button';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { getAlertRuleDisplayName } from '@/utils/alert-rules';
import { WebhookDeliveryLogModal } from './WebhookDeliveryLogModal';

interface ActionsPopoverProps {
    alertRule: FullAlertRule;
    onClose: () => void;
}

const ACTION_TYPE_ICONS: Record<AlertRuleActionType, React.ElementType> = {
    [AlertRuleActionType.EMAIL]: EnvelopeSimpleIcon,
    [AlertRuleActionType.WHATSAPP]: WhatsappLogoIcon,
    [AlertRuleActionType.APP_NOTIFICATION]: BellSimpleIcon,
    [AlertRuleActionType.WEBHOOK]: WebhooksLogoIcon,
};

type AlertRuleActionView = FullAlertRule['actions'][number];

const getActionLabel = (action: AlertRuleActionView) => {
    if (action.recipient?.email) return action.recipient.email;
    if (action.recipient?.phone) return action.recipient.phone;
    if (action.webhook?.url) return action.webhook.url;
    return action.type;
};

export function ActionsPopover({ alertRule, onClose }: ActionsPopoverProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const { t: tAlertRules } = useTranslation(I18nNamespace.ALERT_RULES);
    const [deliveryLogAction, setDeliveryLogAction] =
        useState<AlertRuleActionView | null>(null);

    const actions = alertRule.actions || [];

    return (
        <div className="w-[310px] bg-grey-700 border border-grey-500 rounded-lg backdrop-blur-sm p-4 flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <h3 className="text-sm-medium text-white-100">
                    {t('infoPanel.actions')}
                </h3>
                <button
                    onClick={onClose}
                    className="w-4 h-4 flex items-center justify-center text-grey-300 hover:text-white-100 transition-colors"
                >
                    <XIcon className="w-4 h-4" />
                </button>
            </div>

            <div className="flex flex-col max-h-[280px] overflow-y-auto">
                {actions.length > 0 ? (
                    actions.map((action, index) => {
                        const Icon =
                            ACTION_TYPE_ICONS[action.type] || BellSimpleIcon;
                        const label = getActionLabel(action);
                        const webhookUrl = action.webhook?.url;
                        const labelText = (
                            <span className="text-sm text-white-100 truncate flex-1">
                                {label}
                            </span>
                        );

                        return (
                            <div key={action.id}>
                                <div className="flex items-start gap-1 py-4">
                                    <div className="flex flex-col gap-1 flex-1 min-w-0">
                                        <div className="flex justify-center items-center gap-2 flex-1">
                                            <Icon className="w-4 h-4 text-grey-200 flex-shrink-0" />
                                            {webhookUrl ? (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        {labelText}
                                                    </TooltipTrigger>
                                                    <TooltipContent
                                                        side="top"
                                                        sideOffset={8}
                                                        className="break-all"
                                                    >
                                                        {webhookUrl}
                                                    </TooltipContent>
                                                </Tooltip>
                                            ) : (
                                                labelText
                                            )}
                                        </div>
                                        {action.webhook && (
                                            <Button
                                                variant="tertiary"
                                                size="small"
                                                onClick={() =>
                                                    setDeliveryLogAction(action)
                                                }
                                                leadingIcon={
                                                    <ClockCounterClockwiseIcon
                                                        size={14}
                                                    />
                                                }
                                                className="mt-1 self-start"
                                            >
                                                {tAlertRules(
                                                    'webhookDeliveries.viewLog',
                                                )}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                {index < actions.length - 1 && (
                                    <div className="h-px bg-grey-500" />
                                )}
                            </div>
                        );
                    })
                ) : (
                    <div className="text-sm text-grey-300">
                        {t('infoPanel.noRecipients')}
                    </div>
                )}
            </div>

            {deliveryLogAction && (
                <WebhookDeliveryLogModal
                    alertRuleId={alertRule.id}
                    alertRuleActionId={deliveryLogAction.id}
                    alertRuleName={getAlertRuleDisplayName(alertRule)}
                    webhookUrl={deliveryLogAction.webhook?.url ?? ''}
                    onClose={() => setDeliveryLogAction(null)}
                />
            )}
        </div>
    );
}
